import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'development' 
    ? 'http://localhost:5000/api' 
    : '/api'
);

// Create axios instance with base configuration
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Notification {
  _id: string;
  type: 'appointment_cancelled' | 'appointment_created' | 'appointment_modified' | 'system_alert' | 'user_message';
  title: string;
  message: string;
  metadata: {
    patientId?: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    appointmentId?: {
      _id: string;
      title: string;
      startTime: string;
      status: string;
    };
    userId?: {
      _id: string;
      name: string;
      email: string;
    };
    extra?: any;
  };
  status: 'unread' | 'read' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: string;
  readAt?: string;
  archivedAt?: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  pagination: {
    current: number;
    total: number;
    count: number;
    totalItems: number;
  };
  unreadCount: number;
}

export interface UnreadCountResponse {
  unreadCount: number;
}

export const notificationsApi = {
  // Get all notifications with filters and pagination
  getAll: async (params?: {
    status?: 'unread' | 'read' | 'archived' | 'all';
    type?: string;
    priority?: string;
    limit?: number;
    page?: number;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<NotificationsResponse> => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  // Get unread count only
  getUnreadCount: async (): Promise<UnreadCountResponse> => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },

  // Send user message
  sendMessage: async (message: string): Promise<Notification> => {
    const response = await api.post('/notifications/message', { message });
    return response.data;
  },

  // Create new notification
  create: async (notification: {
    title: string;
    message: string;
    type?: Notification['type'];
    priority?: Notification['priority'];
    metadata?: Partial<Notification['metadata']>;
  }): Promise<Notification> => {
    const response = await api.post('/notifications', notification);
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (id: string): Promise<Notification> => {
    const response = await api.put(`/notifications/${id}/read`);
    return response.data;
  },

  // Archive notification
  archive: async (id: string): Promise<Notification> => {
    const response = await api.put(`/notifications/${id}/archive`);
    return response.data;
  },

  // Mark all notifications as read
  markAllAsRead: async (): Promise<{ message: string; modifiedCount: number }> => {
    const response = await api.put('/notifications/mark-all-read');
    return response.data;
  },

  // Delete notification
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  },

  // Bulk delete notifications
  bulkDelete: async (params: { ids?: string[]; status?: string }): Promise<{ message: string; deletedCount: number }> => {
    const response = await api.delete('/notifications/bulk-delete', { data: params });
    return response.data;
  },
};

export default notificationsApi;